<template>
  <div class="tab-content mt-20">
    <unlock-device-modal @unlockDeviceConfirmation="unlockDevice" :device="unlockDeviceName" />
    <div class="flex justify-between items-center mb-50 flex-wrap gap-20"
    data-intro="device">
    <div class="flex items-center gap-x-20">
      <router-link
      :to="{
        name: 'Dashboard',
        query: { active: 'dashboard' },
        params: { locale: $i18n.locale },
      }"
        >
        <div class="back-icon">
          <back-arrow class="m-auto right-back-arrow" />
        </div>
      </router-link>
      <p class="font-bold text-title">{{ $t('devices') }}</p>
    </div>
    <div class="flex gap-x-20 sm:gap-x-3">
        <base-button
        v-if="hasPermission('zones_and_devices_management') && devices.length >= 1"
          buttonType="primary"
          width="102"
          height="40"
          class="sm:w-90"
          radius="default"
          :hasIcon="true"
          @click.native="AddButtonHandler()"
          >
          <template #text>
            <span class="text-14">
              {{ $t('add') }}
            </span>
          </template>
          <template #icon>
            <img :src="Add" alt="add" class="mx-3" />
          </template>
        </base-button>
        <base-simple-search-box
        id="s_dashboard"
        @searchedText="getSearchedText"
        @searchedIn="getSearchedType"
        customStyle="sm:w-full text-title"
        width="200"
        height="40"
        type="zones"
        />
      </div>
    </div>
    <!-- device component start -->
    <div class="device-card" v-if="isLoading && isLoading.name === 'devices'">
      <div v-for="index in Math.floor(Math.random() * 16)" :key="index" class="device-card-main">
        <div class="flex items-center gap-x-10 mb-10">
          <base-skeleton width="60" height="60" name="devices" />
          <div class="flex flex-col gap-10">
            <base-skeleton width="94" height="15" name="devices" />
            <base-skeleton width="78" height="12" name="devices" />
          </div>
        </div>
        <base-skeleton width="265" height="32" name="devices" />
      </div>
    </div>
    <div v-else>
      <div v-if="devices && devices.length > 0" class="mt-40">
        <div class="device-card">
          <div
            class="device-card-main"
            v-for="(device, index) in devices"
            :key="index"
            @click="goToReservationRoom(device)"
          >
            <div class="flex justify-between">
              <div class="flex items-center gap-x-10">
                <div class="w-60 h-60 bg-tertiary rounded flex justify-center items-center">
                  <img :src="Device" alt="Device" class="block w-30 h-30" />
                </div>
                <div>
                  <h3 class="text-sm font-bold truncate w-180">{{ device.name }}</h3>
                  <h5 class="text-12 font-medium text-subtitle">
                    {{ device.reservable === 1 ? $t('conference_room') : $t('work_room') }}
                  </h5>
                </div>
              </div>
              <div>
                <div class="card-header-icon-success" v-if="device.connected" />
                <div class="card-header-icon-danger" v-else />
              </div>
            </div>
            <div
              class="flex justify-center pt-10"
              @click.stop.prevent="unlockDeviceHandler(device.id, device.name, device.can_unlock)"
            >
              <base-button
                radius="sm"
                buttonType="success-opacity"
                width="full"
                height="32"
                :disabled="device.can_unlock === false || device.can_unlock === null"
              >
                <template #text>
                  <div class="flex justify-center items-center gap-x-3">
                    <lock-icon
                      width="15"
                      height="15"
                      :color="device.can_unlock === false || device.can_unlock === null
                          ? '#BDBEBF'
                          : '#34C759'
                      "
                    />
                    <div class="text-sm">
                      {{ $t('unlock') }}
                    </div>
                  </div>
                </template>
              </base-button>
            </div>
          </div>
        </div>
        <base-paginate :links="links" @onPageChange="onPageChange" type="devices" class="mt-50" />
      </div>
      <!-- device component end -->
      <base-no-item-to-show v-else-if="isSearching" :message="$t('device')" />
      <div class="center my-110" v-else-if="devices && devices.length === 0">
        <base-add-page
          paddingX="60"
          paddingY="150"
          :message="$t('device')"
          v-if="hasPermission('zones_and_devices_management')"
          @click.native="AddButtonHandler()"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import BasePaginate from '@/components/base/BasePaginate.vue';
import BaseAddPage from '@/components/base/BaseAddPage.vue';
import BaseSkeleton from '@/components/base/BaseSkeleton.vue';
import Device from '@/assets/icons/svg/iot-device.svg';
import Lock from '@/assets/icons/svg/lock.svg';
import axiosConfig from '@/services/axios.config';
import serverErrorsHandler from '@/utils/serverErrorsHandler';
import BaseButton from '@/components/base/BaseButton.vue';
import BaseSimpleSearchBox from '@/components/base/BaseSimpleSearchBox.vue';
import Add from '@/assets/icons/svg/white-add.svg';
import BackArrow from '@/assets/icons/vue/backArrow.vue';
import BaseNoItemToShow from '@/components/base/BaseNoItemToShow.vue';
import LockIcon from '@/assets/icons/vue/Lock.vue';
import UnlockDeviceModal from '@/components/modals/UnlockDeviceModal.vue';
import startIntro from '@/utils/startIntro';

export default {
  data() {
    return {
      Device,
      Lock,
      devices: [],
      links: [],
      searchedText: '',
      isSearching: false,
      searchType: '',
      currentPage: 1,
      Add,
      unlockDeviceId: -1,
      unlockDeviceName: '',
      isFirstIntro: true,
    };
  },
  computed: {
    ...mapGetters({
      defaultCompany: 'getDefaultCompany',
      isLoading: 'loading/getActiveSkeletons',
      currentPermission: 'permissions/getPermissions',
      userInformation: 'getUserInformation',
      introList: 'getIntroList',
    }),
    devicesIntroList() {
      return this.introList
            .filter((introItem) => introItem.page === 'devices')
            .map((item) => ({
              id: item.id,
              element: `[data-intro='${item.element}']`,
              intro: item.content,
            }));
    },
    isOwner() {
      return this.$store.state.permissions.isOwner;
    },
  },
  components: {
    BaseAddPage,
    BaseSkeleton,
    BasePaginate,
    BaseButton,
    BaseSimpleSearchBox,
    BackArrow,
    BaseNoItemToShow,
    LockIcon,
    UnlockDeviceModal,
  },
  mounted() {
    this.getDevices('');
    setTimeout(() => {
      this.introHandler();
    }, 2000);
  },
  methods: {
    introHandler() {
      if (this.isFirstIntro && this.userInformation.is_intro_enable
       && this.devicesIntroList.length > 0) {
        this.isFirstIntro = false;
         setTimeout(() => {
          startIntro(this.devicesIntroList);
         }, 1000);
      }
    },
    unlockDeviceHandler(id, name, canUnlock) {
      this.unlockDeviceId = id;
      this.unlockDeviceName = name;
      if (canUnlock) {
        this.$store.dispatch('modal/open', 'unlockDeviceModal');
      }
    },
    closeModals(...modals) {
      modals.map((modalName) => this.$store.dispatch('modal/close', modalName));
    },
    unlockDevice() {
      this.$store.commit('loading/DISABLE_HANDLER', true);
      axiosConfig
        .get(`/companies/${this.defaultCompany.id}/devices/${this.unlockDeviceId}/unlock`)
        .then(() => {
          this.closeModals('unlockDeviceModal');
          this.$toast.success(this.$t('unlocked_successfully', { device: this.unlockDeviceName }));
          this.$store.commit('loading/DISABLE_HANDLER', false);
        })
        .catch((error) => {
          this.closeModals('unlockDeviceModal');
          serverErrorsHandler(error);
          this.$store.commit('loading/DISABLE_HANDLER', false);
        });
    },
    AddButtonHandler() {
      this.$router.push({
        name: 'AddDevice',
        params: { parentName: this.defaultCompany.name },
      });
    },
    onPageChange(page, type) {
      this.$store.dispatch('loading/skeletonLoader', {
        name: type,
        status: true,
      });
      this.currentPage = page;
      this.getDevices();
    },
    getSearchedType(type) {
      this.searchType = type;
    },
    getSearchedText(text) {
      this.searchedText = text;
      this.isSearching = !!(text && text !== '');
      this.getDevices();
    },
    hasPermission(permission) {
      return !!(this.currentPermission && this.currentPermission.includes(permission));
    },
    goToReservationRoom(item) {
      if (item.reservable === 1) {
        this.$router.push({
          name: 'ConferenceRoom',
          params: { id: item.id },
          query: { date: new Date().toISOString() },
        });
      } else {
        this.$router.push({ name: 'WorkRoom', params: { id: item.id } });
      }
    },
    getDevices() {
      this.$store.dispatch('loading/skeletonLoader', {
        name: 'devices',
        status: true,
      });
      axiosConfig
        .get(
          `/companies/${this.defaultCompany.id}/devices?per_page=16&page=${this.currentPage}&q=${
            this.searchedText || ''
          }&columns[]=name`,
        )
        .then((response) => {
          this.devices = response.data.data;
          this.links = response.data.meta;
          this.$store.dispatch('loading/skeletonLoader', {
            name: 'devices',
            status: false,
          });
        })
        .catch((error) => {
          this.$store.dispatch('loading/skeletonLoader', {
            name: 'devices',
            status: false,
          });
          serverErrorsHandler(error);
        });
    },
  },
};
</script>

<style scoped>
.device-card-main {
  @apply bg-secondary cursor-pointer h-128 w-294 rounded border border-default flex flex-col p-12;
}
.device-card {
  @apply grid grid-cols-4 md:grid-cols-2 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-30;
}
.card-header-icon-success,
.card-header-icon-danger {
  @apply w-8 h-8 rounded-full opacity-40 self-center shadow-icon flex;
}
.card-header-icon-success {
  @apply bg-success;
}
.card-header-icon-danger {
  @apply bg-danger;
}
.back-icon {
  @apply w-40 h-40 rounded flex border-2 border-default left-50 cursor-pointer;
}
.tab-content {
  @apply bg-secondary py-40 px-30 rounded;
}
</style>
